import { useMemo } from "react";

import { useDateConfig } from "./DateContext";

/**
 * Contract dates — start, expiry, birthday — are *calendar dates*, not
 * instants. "Expires 25 September" means the 25th; there is no meaningful time
 * of day attached. The API stores them in a DateTime column, so they make a
 * round trip through an instant, and that round trip is where they were being
 * bent.
 *
 * Two defects came out of it:
 *
 *  1. `new Date("2026-09-25")` parses date-only strings as **UTC** midnight.
 *     Rendered in IST that is "25 Sept, 05:30 am" — the right day with a time
 *     nobody entered, which is the drift visible on every save. West of UTC it
 *     is worse than cosmetic: UTC midnight renders as the *previous day*, so a
 *     contract silently expires a day early.
 *
 *  2. Reading back with `value.split("T")[0]` takes the **UTC** date part. A
 *     row stored at 18:30Z (midnight IST) hands the date editor the previous
 *     day — and saving that shifts the record back a day, every time.
 *
 * The zone that decides which calendar day an instant falls on is the
 * **organisation's**, not the browser's. Two people opening the same contract
 * from different countries must see the same expiry date, and that date has to
 * be the one the organisation operates on. So every function here takes the
 * time zone explicitly; `useCalendarDate()` binds them to the configured one.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** The offset, in ms, between an instant and the wall clock it shows in `timeZone`. */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);

  const at = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");

  // `hour` comes back as 24 rather than 0 at midnight in some engines.
  const asIfUtc = Date.UTC(
    at("year"),
    at("month") - 1,
    at("day"),
    at("hour") % 24,
    at("minute"),
    at("second"),
  );

  return asIfUtc - instant.getTime();
}

/**
 * An instant (or date-only string) as the `YYYY-MM-DD` an `<input type="date">`
 * wants, resolved in `timeZone` — so the editor opens on the same date the page
 * is displaying rather than on its UTC counterpart.
 */
export function toDateInputValue(
  value: string | number | Date | null | undefined,
  timeZone: string,
): string {
  if (!value) return "";
  if (typeof value === "string") {
    if (DATE_ONLY.test(value)) return value;
    // The API's "unset" sentinel, which would otherwise render as year 1.
    if (value.startsWith("0001")) return "";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const at = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${at("year")}-${at("month")}-${at("day")}`;
}

/**
 * A `YYYY-MM-DD` from a date input as the ISO instant the API takes, anchored
 * at midnight **in `timeZone`**. Round-tripped through `toDateInputValue` with
 * the same zone it gives back the same calendar date, which is the point.
 *
 * A value that is already an instant passes through untouched — this must never
 * re-anchor a real timestamp.
 */
export function toCalendarDateIso(
  value: string | number | Date | null | undefined,
  timeZone: string,
): string | null {
  if (!value) return null;

  if (typeof value === "string" && DATE_ONLY.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const wallClock = Date.UTC(year, month - 1, day);

    // Solve for the instant whose wall clock in `timeZone` is that midnight.
    // Two passes: the first offset is read at the wrong instant when the guess
    // lands on the far side of a DST boundary, the second corrects it.
    let instant = wallClock - zoneOffsetMs(new Date(wallClock), timeZone);
    instant = wallClock - zoneOffsetMs(new Date(instant), timeZone);

    // On a spring-forward date midnight does not exist — Santiago jumps
    // 00:00 to 01:00 on 6 Sep 2026, and the solve above lands on 23:00 the
    // day before, which reads back as the wrong day. Step forward to the
    // first instant that does fall on the requested date. Bounded, so a zone
    // rule we haven't anticipated degrades to the original guess rather than
    // spinning.
    const HOUR = 60 * 60 * 1000;
    for (let i = 0; i < 4; i++) {
      if (toDateInputValue(new Date(instant), timeZone) === value) break;
      instant += HOUR;
    }

    return new Date(instant).toISOString();
  }

  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
}

/** Today's calendar date in `timeZone`, as `YYYY-MM-DD`. */
export function todayDateInputValue(timeZone: string): string {
  return toDateInputValue(new Date(), timeZone);
}

/**
 * The three above, bound to the organisation's configured time zone. Use this
 * in components rather than calling them with a hand-picked zone.
 */
export function useCalendarDate() {
  const { timezone } = useDateConfig();

  return useMemo(
    () => ({
      timeZone: timezone,
      toDateInputValue: (value: string | number | Date | null | undefined) =>
        toDateInputValue(value, timezone),
      toCalendarDateIso: (value: string | number | Date | null | undefined) =>
        toCalendarDateIso(value, timezone),
      today: () => todayDateInputValue(timezone),
    }),
    [timezone],
  );
}
